import { useEffect, useRef, useState } from 'react';

// ── Exam-relevant COCO class filter ──────────────────────────

export interface Detection {
  /** COCO class name (English) */
  label: string;
  /** Korean display name */
  labelKo: string;
  /** Detection confidence [0, 1] */
  score: number;
  /** Bounding box in pixel coordinates */
  bbox: { x: number; y: number; w: number; h: number };
  /** Normalized bounding box [0, 1] */
  bboxNorm: { x: number; y: number; w: number; h: number };
  /** Salience level for proctoring */
  salience: 'none' | 'low' | 'high';
}

const EXAM_CLASSES: Record<string, { ko: string; salience: Detection['salience'] }> = {
  'cell phone': { ko: '핸드폰', salience: 'high' },
  'book':       { ko: '교재/책', salience: 'high' },
  'laptop':     { ko: '노트북', salience: 'high' },
  'remote':     { ko: '리모컨', salience: 'low' },
  'mouse':      { ko: '마우스', salience: 'none' },
  'keyboard':   { ko: '키보드', salience: 'none' },
  'person':     { ko: '사람', salience: 'low' },
  'clock':      { ko: '시계', salience: 'none' },
  'scissors':   { ko: '가위', salience: 'none' },
  'bottle':     { ko: '물병', salience: 'none' },
  'cup':        { ko: '컵', salience: 'none' },
};

// ── Hook ─────────────────────────────────────────────────────

export interface UseObjectDetectorReturn {
  detections: Detection[];
  isReady: boolean;
  fps: number;
}

const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float16/1/efficientdet_lite0.tflite';
const DETECT_INTERVAL_MS = 200; // ~5 fps
const SCORE_THRESHOLD = 0.35;

/**
 * MediaPipe Object Detector hook — runs EfficientDet-Lite0 (COCO 80 classes)
 * on the webcam stream at ~5 fps. Filters to exam-relevant classes and returns
 * bounding boxes + Korean labels + salience levels.
 *
 * Uses the same @mediapipe/tasks-vision package as FaceLandmarker (no new deps).
 * Model is ~4.4 MB (float16), loaded lazily on first enable.
 */
export function useObjectDetector(
  videoElementId: string,
  enabled: boolean,
): UseObjectDetectorReturn {
  const [detections, setDetections] = useState<Detection[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [fps, setFps] = useState(0);

  const detectorRef = useRef<any>(null);
  const initRef = useRef(false);
  const animRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (initRef.current) return;
    initRef.current = true;

    let disposed = false;

    const init = async () => {
      try {
        const vision = await import('@mediapipe/tasks-vision');
        const { ObjectDetector, FilesetResolver } = vision;

        const filesetResolver = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm',
        );

        const detector = await ObjectDetector.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath: MODEL_URL,
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          maxResults: 8,
          scoreThreshold: SCORE_THRESHOLD,
        });

        if (disposed) return;
        detectorRef.current = detector;
        setIsReady(true);

        // Start detection loop
        let lastDetectTime = 0;
        let frameCount = 0;
        let fpsTimer = performance.now();

        const detect = () => {
          if (disposed) return;
          animRef.current = requestAnimationFrame(detect);

          const now = performance.now();
          if (now - lastDetectTime < DETECT_INTERVAL_MS) return;

          const videoEl = document.getElementById(videoElementId) as HTMLVideoElement | null;
          if (!videoEl || !videoEl.videoWidth || videoEl.readyState < 2) return;

          lastDetectTime = now;

          try {
            const result = detectorRef.current.detectForVideo(videoEl, now);

            // Map to our Detection type, filtering to exam-relevant classes
            const mapped: Detection[] = [];
            if (result.detections) {
              const vw = videoEl.videoWidth;
              const vh = videoEl.videoHeight;

              for (const det of result.detections) {
                if (!det.categories || det.categories.length === 0) continue;
                const cat = det.categories[0];
                const classInfo = EXAM_CLASSES[cat.categoryName];
                if (!classInfo) continue; // skip non-exam classes

                const bb = det.boundingBox;
                if (!bb) continue;

                mapped.push({
                  label: cat.categoryName,
                  labelKo: classInfo.ko,
                  score: cat.score,
                  bbox: {
                    x: bb.originX,
                    y: bb.originY,
                    w: bb.width,
                    h: bb.height,
                  },
                  bboxNorm: {
                    x: bb.originX / vw,
                    y: bb.originY / vh,
                    w: bb.width / vw,
                    h: bb.height / vh,
                  },
                  salience: classInfo.salience,
                });
              }
            }

            setDetections(mapped);

            // FPS counter
            frameCount++;
            if (now - fpsTimer > 1000) {
              setFps(frameCount);
              frameCount = 0;
              fpsTimer = now;
            }
          } catch {
            // Ignore transient detection errors
          }
        };

        animRef.current = requestAnimationFrame(detect);
      } catch (err) {
        console.error('[useObjectDetector] Init failed:', err);
      }
    };

    init();

    return () => {
      disposed = true;
      cancelAnimationFrame(animRef.current);
    };
  }, [enabled, videoElementId]);

  return { detections, isReady, fps };
}
